import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authAPI } from '../lib/api';
import { ADMIN_FILTER_SCHLUESSEL } from '../lib/dashboardMonat';
import { loginErgebnis, LOGIN_MERKZEICHEN } from '../lib/zweiFaktor';
import { VERWALTUNGS_ROLLEN } from '../lib/rollen';
import { merkerFuer, merkerSpeichern } from '../lib/geraet';
import { sollAuffrischen, sollVorabAbmelden, tokenUebernehmen } from '../lib/sitzungAuffrischen';

const ANMELDUNG_UNVOLLSTAENDIG = 'Die Anmeldung konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut.';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  // S1: Sitzung auffrischen - beim Start und wenn die Seite wieder sichtbar wird, höchstens einmal in 60 Minuten.
  const letzteAuffrischung = useRef(0);
  const logoutRef = useRef(null);
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const auffrischen = async () => {
      if (document.visibilityState === 'hidden' || !sollAuffrischen(letzteAuffrischung.current, Date.now())) return;
      letzteAuffrischung.current = Date.now();
      const tokenBeiStart = localStorage.getItem('token');
      try {
        const { data } = await authAPI.sitzung();
        if (sollVorabAbmelden(data.spaetestens_bis, Date.now())) {
          logoutRef.current();
          window.location.assign('/login?grund=30tage');
          return;
        }
        if (tokenUebernehmen(tokenBeiStart, localStorage.getItem('token'), data.access_token)) localStorage.setItem('token', data.access_token);
      } catch { /* Netzfehler: nichts ändern; 401 regeln lib/api.js und lib/sitzung.js */ }
    };
    auffrischen();
    window.addEventListener('focus', auffrischen);
    document.addEventListener('visibilitychange', auffrischen);
    return () => {
      window.removeEventListener('focus', auffrischen);
      document.removeEventListener('visibilitychange', auffrischen);
    };
  }, [isAuthenticated]);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (token && storedUser) {
      try {
        const response = await authAPI.getMe();
        setUser(response.data);
        setIsAuthenticated(true);
        setMustChangePassword(response.data.must_change_password || false);
      } catch (error) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setIsAuthenticated(false);
        setMustChangePassword(false);
      }
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    // Geteilte Büro-PCs: die gemerkten Filter des Vorgängers dürfen nicht stehen bleiben.
    // Auch nötig, weil ein abgelaufenes Token ohne logout auf /login umleitet.
    try { sessionStorage.removeItem(ADMIN_FILTER_SCHLUESSEL); } catch { /* privater Modus */ }
    const response = await authAPI.login(email, password, merkerFuer(email));
    merkerSpeichern(email, response.data?.geraet_merker);
    const ergebnis = loginErgebnis(response.data);
    // Ohne access_token nichts speichern: sonst ließe ProtectedRoute schon durch.
    if (ergebnis.art === 'zwei_faktor') return { zweiFaktor: ergebnis.zweiFaktor, zwischenToken: ergebnis.zwischenToken };
    if (ergebnis.art !== 'fertig') return { fehler: ANMELDUNG_UNVOLLSTAENDIG };
    return anmeldungAbschliessen(response.data);
  };

  const anmeldungAbschliessen = ({ access_token, user: userData, must_change_password }) => {
    if (!access_token) return;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    if (VERWALTUNGS_ROLLEN.includes(userData?.role)) {
      try { sessionStorage.setItem(LOGIN_MERKZEICHEN, '1'); } catch { /* privater Modus */ }
    }
    setUser(userData);
    setIsAuthenticated(true);
    setMustChangePassword(must_change_password || false);
    return { userData, must_change_password };
  };

  // /auth/me neu lesen (2FA-Stand). Bei Fehlern bleibt alles, wie es ist - nie abmelden.
  const kontoNeuLaden = useCallback(async () => {
    try {
      const response = await authAPI.getMe();
      setUser(response.data);
    } catch { /* Stand bleibt */ }
  }, []);

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    try {
      sessionStorage.removeItem(ADMIN_FILTER_SCHLUESSEL);
      sessionStorage.removeItem(LOGIN_MERKZEICHEN);
    } catch { /* privater Modus */ }
    setUser(null);
    setIsAuthenticated(false);
    setMustChangePassword(false);
  };

  const clearMustChangePassword = () => {
    setMustChangePassword(false);
  };

  logoutRef.current = logout;

  // Neues Token nach eigenem Passwortwechsel u. ä. übernehmen - der Nutzer bleibt angemeldet.
  const uebernehmeToken = (token) => {
    if (token) localStorage.setItem('token', token);
  };

  const isAdmin = () => user?.role === 'admin';
  const isAdminLike = () => VERWALTUNGS_ROLLEN.includes(user?.role);
  const isFullAccess = () => user?.role === 'admin' || user?.role === 'geschaeftsfuehrung';

  const value = {
    user,
    loading,
    isAuthenticated,
    mustChangePassword,
    login,
    anmeldungAbschliessen,
    kontoNeuLaden,
    logout,
    isAdmin,
    isAdminLike,
    isFullAccess,
    checkAuth,
    clearMustChangePassword,
    uebernehmeToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
